// Portfolio data shared by the portfolio pages, the phone header and «Профиль» (docs/design/SPEC.md,
// stage M2): accounts, composite portfolios, goals, rates, the selected view and broker refreshes.
import axios from 'axios';
import { computed, ref } from 'vue';
import { extractApiError } from './apiError';
import { t } from './i18n';
import { CAPITAL_KEY, accountView, capitalView, groupView, syncedToday } from './portfolioView';

export const portfolios = ref([]);
export const groups = ref([]);
export const payoutGoals = ref([]);
// Today's CBR rates { USD: 83.6, … } (docs/fx/SPEC.md, decision 6)
export const fxRates = ref({});
// IIS deposits this year against the deduction limit, null when off or no IIS (docs/iis/SPEC.md).
export const iisLimit = ref(null);
export const keyConfigured = ref(false);
export const loading = ref(true);
// True after the first successful GET /portfolios for the signed-in user.
export const loaded = ref(false);
export const busy = ref(false);
export const error = ref('');
export const message = ref('');
// Bumped whenever accounts change: pages reload what depends on them (payout calendar).
export const dataVersion = ref(0);

// Selected portfolio (docs/portfolios/SPEC.md, decision 16). localStorage is only a convenience.
const SELECTED_STORAGE_KEY = 'portfolios.selected';

function readSelected() {
  try {
    return window.localStorage.getItem(SELECTED_STORAGE_KEY) || CAPITAL_KEY;
  } catch {
    return CAPITAL_KEY;
  }
}

export const selectedKey = ref(readSelected());

export function select(key) {
  selectedKey.value = key;
  try {
    window.localStorage.setItem(SELECTED_STORAGE_KEY, key);
  } catch {
    // Storage blocked: the choice lasts until reload
  }
}

export const views = computed(() => {
  const byId = new Map(portfolios.value.map((p) => [p.id, p]));
  return [
    capitalView(portfolios.value, t('portfolios.capital'), fxRates.value),
    ...portfolios.value.map(accountView),
    ...groups.value.map((g) => groupView(g, byId, fxRates.value)),
  ];
});

// A removed account or group falls back to "Capital".
export const currentView = computed(() => views.value.find((v) => v.key === selectedKey.value) ?? views.value[0]);

function importedMessage(data) {
  const skipped = data.skipped?.length ?? 0;
  return skipped > 0
    ? `${data.message} ${t('portfolios.skipped', { count: skipped })}`
    : data.message;
}

function setPortfolios(list, rates, iis) {
  portfolios.value = list;
  if (rates) {
    fxRates.value = rates;
  }
  if (iis !== undefined) {
    iisLimit.value = iis;
  }
  dataVersion.value++;
}

function clearNotices() {
  error.value = '';
  message.value = '';
}

export async function loadPortfolios({ silent = false } = {}) {
  if (!silent) {
    loading.value = true;
  }
  error.value = '';
  try {
    const { data } = await axios.get('/portfolios');
    groups.value = data.groups ?? [];
    payoutGoals.value = data.payout_goals ?? [];
    keyConfigured.value = data.tinvest_key_configured;
    setPortfolios(data.portfolios, data.fx_rates ?? {}, data.iis ?? null);
    loaded.value = true;
    refreshStale();
  } catch (e) {
    if (e?.response?.status !== 401) {
      error.value = extractApiError(e, t('portfolios.loadFailed'));
    }
  } finally {
    loading.value = false;
  }
}

export function resetPortfolios() {
  portfolios.value = [];
  groups.value = [];
  payoutGoals.value = [];
  iisLimit.value = null;
  keyConfigured.value = false;
  loaded.value = false;
  loading.value = true;
  clearNotices();
}

// Opened with a snapshot from before today (the nightly import and its retries missed it): the accounts
// are loaded again quietly, «Сегодня» is hidden until then (docs/portfolios/SPEC.md, decision 24).
function refreshStale() {
  const stale = portfolios.value.filter((p) => p.source === 'TBANK' && !syncedToday(p.synced_at));
  if (keyConfigured.value && stale.length > 0) {
    refresh(stale.map((p) => p.account_id), { quiet: true });
  }
}

// Import from T-Invest: the given accounts, or all loaded ones. Resolves to true on success.
// quiet: no message and no error, for the automatic refresh.
export async function refresh(accountIds = portfolios.value.map((p) => p.account_id), { quiet = false } = {}) {
  if (busy.value) {
    return false;
  }
  busy.value = true;
  clearNotices();
  try {
    const { data } = await axios.post('/portfolios/tinvest/import', { account_ids: accountIds });
    setPortfolios(data.portfolios, data.fx_rates, data.iis);
    if (!quiet) {
      message.value = importedMessage(data);
    }
    return true;
  } catch (e) {
    if (!quiet && e?.response?.status !== 401) {
      error.value = extractApiError(e, t('portfolios.refreshFailed'));
    }
    return false;
  } finally {
    busy.value = false;
  }
}

export async function removeAccount(portfolio) {
  if (!window.confirm(t('portfolios.confirmRemove', { name: portfolio.name || portfolio.account_id }))) {
    return;
  }
  busy.value = true;
  clearNotices();
  try {
    const { data } = await axios.delete(`/portfolios/${portfolio.id}`);
    groups.value = data.groups ?? groups.value;
    setPortfolios(data.portfolios);
    message.value = data.message;
  } catch (e) {
    if (e?.response?.status !== 401) {
      error.value = extractApiError(e, t('portfolios.removeFailed'));
    }
  } finally {
    busy.value = false;
  }
}

export async function removeGroup(group) {
  if (!window.confirm(t('portfolios.confirmRemoveGroup', { name: group.name }))) {
    return;
  }
  busy.value = true;
  clearNotices();
  try {
    const { data } = await axios.delete(`/portfolio-groups/${group.id}`);
    groups.value = data.groups;
    message.value = data.message;
  } catch (e) {
    if (e?.response?.status !== 401) {
      error.value = extractApiError(e, t('portfolios.removeGroupFailed'));
    }
  } finally {
    busy.value = false;
  }
}

// A view from the selector: an account or a composite portfolio.
export function remove(view) {
  return view.kind === 'group' ? removeGroup(view.group) : removeAccount(view);
}

// Dialogs opened from the portfolio page and from «Профиль»; App renders them once.
export const importDialogOpen = ref(false);
export const groupDialogOpen = ref(false);
export const editedGroup = ref(null);

export function openGroupDialog(group = null) {
  editedGroup.value = group;
  groupDialogOpen.value = true;
}

export function applyImported(data) {
  importDialogOpen.value = false;
  setPortfolios(data.portfolios, data.fx_rates, data.iis);
  error.value = '';
  message.value = importedMessage(data);
}

export function applyGroupSaved(data) {
  groupDialogOpen.value = false;
  groups.value = data.groups;
  error.value = '';
  message.value = data.message;
  select(`g:${data.group.id}`);
}
